import * as Api from '@ViewModels';
import { useErrorMessages, useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { createContentStateWithHtmlStringValue } from '../../../../models/UiUtils';
import {
	invalidateAllHwcIndustryCategories,
	invalidateAllHwcIndustryCategoryTemplates,
	useHwcCreateTemplateMutation,
	useImageAttachmentMutation,
} from '../../../../queries';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { useHwcTemplateForm } from './hooks/useHwcTemplateForm';
import { HwcTemplateForm } from './HwcTemplateForm';

export function HwcCreateTemplate() {
	const formProps = useHwcTemplateForm();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const logger = useEventLogging('HwcCreateTemplate');
	const imageAttachmentMutation = useImageAttachmentMutation({
		onError: error => {
			errorMessages.push({
				messages: ['Failed to upload image'],
			});
			logger.logApiError('imageAttachmentMutation', error);
		},
	});
	const hwcTemplateMutation = useHwcCreateTemplateMutation({
		onError: error => {
			errorMessages.push({
				messages: ['Failed to create template'],
			});
			logger.logApiError('hwcTemplateMutation', error);
		},
		onSuccess: () => {
			invalidateAllHwcIndustryCategoryTemplates();
			invalidateAllHwcIndustryCategories();
			fullscreenModal.dismissModal();
		},
	});
	const mutationsInProgress = imageAttachmentMutation.isPending || hwcTemplateMutation.isPending;
	const createTemplate = async ({ statuses }: { statuses: Api.ITemplateStatus[] }) => {
		let attachments: Api.IFileAttachment[];
		try {
			attachments = await imageAttachmentMutation.mutateAsync({ files: [formProps.templateImageFile] });
		} catch {
			return;
		}
		const content = createContentStateWithHtmlStringValue(formProps.postcardContent).getRawRichTextContent();
		const payload = {
			statuses,
			template: {
				attachments,
				content,
				keywords: formProps.keywords
					.split(',')

					.map(k => k.trim())

					.filter(k => !!k),
				name: formProps.displayName,
				providerProductIds: formProps.providerProductIds,
				summary: formProps.summary,
				templateType: Api.TemplateType.HandwrittenCard,
				...(!!attachments && {
					imageTypes: {
						[Api.CardImageType.Face]: attachments[0].id,
					},
				}),
			},
		};
		hwcTemplateMutation.mutate({ payload });
	};
	const onSave = async () => {
		if (formProps.validate()) {
			createTemplate({
				statuses: formProps.selectedIndustryOptions.map(({ category, industry }) => ({
					category,
					industry,
					status: Api.TemplateStatus.Draft,
				})),
			});
		}
	};

	const onPublish = async () => {
		if (formProps.validate()) {
			createTemplate({
				statuses: formProps.selectedIndustryOptions

					.map(({ category, industry }) => ({
						category,
						industry,
						status: Api.TemplateStatus.Published,
					}))
					.concat(
						formProps.featured
							? formProps.selectedIndustryOptions.map(({ industry }) => ({
									category: `Featured`,
									industry,
									status: Api.TemplateStatus.Published,
								}))
							: []
					),
			});
		}
	};
	return (
		<>
			<MultiContainerHeader fullscreenHeader='Create New Handwritten Card Template' />
			<HwcTemplateForm {...formProps} onSave={onSave} onPublish={onPublish} mutationInProgress={mutationsInProgress} />
		</>
	);
}
